import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHelmet from '@/components/base/SeoHelmet';
import { categories } from '@/mocks/categories';
import { getFeaturedArticles, getLatestArticles } from '@/mocks/articles';

const colorMap: Record<string, { bg: string; text: string; border: string }> = {
  accent: {
    bg: 'bg-accent-100',
    text: 'text-accent-700',
    border: 'border-accent-200',
  },
  primary: {
    bg: 'bg-primary-100',
    text: 'text-primary-700',
    border: 'border-primary-200',
  },
  secondary: {
    bg: 'bg-secondary-100',
    text: 'text-secondary-700',
    border: 'border-secondary-200',
  },
};

export default function Home() {
  const featuredArticles = getFeaturedArticles();
  const latestArticles = getLatestArticles(6);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '免费91 网页',
    url: 'https://free91.com',
    description: '免费91 网页为您提供便捷的免费在线访问体验，汇聚丰富网页资源与导航服务。无需注册，直接访问免费91 网页，享受流畅的网络内容浏览服务。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://free91.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '免费91 网页',
    url: 'https://free91.com',
    description: '免费在线访问入口与资源导航平台',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-991-0000',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  return (
    <>
      <SeoHelmet
        title="免费在线访问入口与资源导航"
        description="免费91 网页为您提供便捷的免费在线访问体验，汇聚丰富网页资源与导航服务。无需注册，直接访问免费91 网页，享受流畅的网络内容浏览服务，是您发现优质免费网页资源的首选平台。"
        keywords="免费91 网页,免费91网页入口,91网页免费访问,免费在线网页导航,91免费资源网页,网页免费浏览平台,免费91在线入口"
        canonicalUrl="https://free91.com"
        ogImage="https://readdy.ai/api/search-image?query=modern%20minimalist%20website%20resource%20navigation%20portal%20hero%20image%2C%20warm%20coral%20and%20amber%20color%20scheme%2C%20clean%20design%2C%20digital%20content%20concept%2C%20soft%20gradient%20background%2C%20professional%20aesthetic&width=1200&height=630&seq=home-og-001&orientation=landscape"
        schema={[websiteSchema, orgSchema]}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative min-h-[520px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20warm%20gradient%20background%20with%20soft%20flowing%20shapes%20in%20coral%20and%20amber%20tones%2C%20minimalist%20geometric%20patterns%2C%20modern%20digital%20art%20style%2C%20clean%20smooth%20texture%2C%20bright%20and%20airy%20atmosphere%2C%20subtle%20depth%2C%20professional%20web%20design%20background&width=1600&height=900&seq=hero-bg-001&orientation=landscape"
              alt="免费91 网页背景"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/20"></div>
          </div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white mb-4 leading-tight">
              发现优质免费网页资源
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              免费91 网页为您提供便捷的免费在线访问体验，汇聚丰富网页资源与导航服务。无需注册，直接访问，享受流畅的网络内容浏览服务，是您发现优质免费网页资源的首选平台。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/free-tools"
                className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap text-sm shadow-lg shadow-primary-500/25"
              >
                探索免费资源
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto px-8 py-3.5 bg-white/15 backdrop-blur-sm text-white font-medium rounded-full hover:bg-white/25 transition-all duration-300 whitespace-nowrap text-sm border border-white/20"
              >
                了解更多
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
              {[
                { value: '1000+', label: '免费资源' },
                { value: '6', label: '资源分类' },
                { value: '50万+', label: '月访问用户' },
                { value: '100%', label: '永久免费' },
              ].map((stat, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <div className="text-xl md:text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-white/60 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                资源分类导航
              </h2>
              <p className="mt-3 text-sm text-foreground-600 max-w-lg mx-auto">
                按类别浏览免费资源，快速找到你需要的优质内容
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categories.map((cat) => {
                const c = colorMap[cat.color] || colorMap.primary;
                return (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl ${c.bg} flex items-center justify-center flex-shrink-0`}>
                        <i className={`${cat.icon} text-xl ${c.text}`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors">
                          {cat.name}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
                          {cat.description}
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <span className="text-xs text-foreground-400">{cat.articleCount} 篇资源</span>
                          <i className="ri-arrow-right-line text-xs text-foreground-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all"></i>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  精选推荐
                </h2>
                <p className="mt-2 text-sm text-foreground-600">编辑精选优质免费资源文章</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {featuredArticles.map((article, idx) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                >
                  <div className="flex flex-col sm:flex-row">
                    <div className="sm:w-48 h-44 sm:h-auto flex-shrink-0 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 p-4 md:p-5 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
                            {categories.find((c) => c.slug === article.category)?.name}
                          </span>
                          <span className="text-xs text-foreground-400">{article.readTime}</span>
                        </div>
                        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
                          {article.excerpt}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mt-3 text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.author}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  最新文章
                </h2>
                <p className="mt-2 text-sm text-foreground-600">最新发布的免费资源推荐文章</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">
                        {categories.find((c) => c.slug === article.category)?.name}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 mt-3 text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span>·</span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                为什么选择免费91 网页
              </h2>
              <p className="mt-3 text-sm text-foreground-600 max-w-lg mx-auto">
                我们致力于为您提供最优质的免费资源导航体验
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '所有资源经过人工审核，确保安全无毒，放心使用' },
                { icon: 'ri-flashlight-line', title: '完全免费', desc: '无需注册，无需付费，所有资源完全免费开放使用' },
                { icon: 'ri-refresh-line', title: '持续更新', desc: '团队每日更新资源库，确保内容新鲜度和时效性' },
                { icon: 'ri-user-heart-line', title: '用户体验', desc: '简洁直观的界面设计，快速找到目标资源' },
              ].map((item, i) => (
                <div key={i} className="text-center p-5">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-2xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                常见问题
              </h2>
              <p className="mt-3 text-sm text-foreground-600">关于免费91 网页的常见疑问解答</p>
            </div>

            <div className="space-y-3">
              {[
                {
                  q: '免费91 网页需要注册才能使用吗？',
                  a: '不需要。免费91 网页完全免费开放，无需注册即可直接访问所有资源导航内容，真正实现零门槛使用。',
                },
                {
                  q: '免费91 网页上的资源都是免费的吗？',
                  a: '是的。我们严格筛选推荐的资源均为免费或提供免费版本，确保用户始终能够免费获取所需的服务和内容。',
                },
                {
                  q: '免费91 网页的资源多久更新一次？',
                  a: '我们的编辑团队每天都会对资源库进行更新和维护，确保推荐的内容始终保持新鲜和有效。同时会根据用户反馈及时调整资源列表。',
                },
                {
                  q: '如何联系免费91 网页的客服团队？',
                  a: '您可以通过邮箱 contact@free91.com 或客服热线 400-991-0000 联系我们，工作时间周一至周五 9:00-18:00。也可以访问"关于我们"页面了解更多联系方式。',
                },
                {
                  q: '免费91 网页支持手机访问吗？',
                  a: '完全支持。免费91 网页采用响应式设计，在手机、平板和电脑上都能获得流畅的浏览体验，随时随地访问免费资源。',
                },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-xs text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}